// src/watchface/host.ts
// Absorbed from watchface-host.js (Task 33)

import type {
    WatchfaceBase,
    ClockPayload,
    LockLineColliderSegment,
    LockLineColliderOptions,
    WatchfaceConfigParam,
} from './base';

/** Minimal storage interface that matches both localStorage and Loop.storage. */
interface StorageLike {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
}

/** Build options passed to each WatchfaceBase subclass constructor. */
interface FaceOptions {
    width: number;
    height: number;
    dpr: number;
}

/** Options accepted by mountFace(). */
interface MountFaceOptions {
    renderNow?: boolean;
}

/** Options accepted by the WatchfaceHost constructor. */
interface WatchfaceHostOptions {
    keepAliveFaceIndices?: number[];
    onFaceChanged?: (face: WatchfaceBase, FaceClass: FaceClass) => void;
}

/** A cached (keep-alive) face entry. */
interface FaceCacheEntry {
    face: WatchfaceBase;
    layer: HTMLDivElement;
}

/** Concrete face class — constructor + static methods that all registered faces must satisfy. */
interface FaceClass {
    new (container: HTMLElement, options: FaceOptions): WatchfaceBase;
    name: string;
    getConfigSchema(): WatchfaceConfigParam[];
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

export class WatchfaceHost {
    container: HTMLElement;
    options: WatchfaceHostOptions;
    registry: FaceClass[];
    activeFace: WatchfaceBase | null;
    activeLayer: HTMLDivElement | null;
    activeIndex: number;
    isSwapping: boolean;
    clockRafId: number | null;
    _clockGen: number;
    _faceCache: Map<number, FaceCacheEntry>;
    keepAliveFaceIndices: Set<number>;
    _dateFormatter: Intl.DateTimeFormat;
    _cachedDateKey: string;
    _cachedDateLabel: string;

    constructor(container: HTMLElement, options: WatchfaceHostOptions = {}) {
        this.container = container;
        this.options = options;
        this.registry = [];
        this.activeFace = null;
        this.activeLayer = null;
        this.activeIndex = 0;
        this.isSwapping = false;
        this.clockRafId = null;
        this._clockGen = 0;
        this._faceCache = new Map();
        const keepAliveFaceIndices = Array.isArray(options.keepAliveFaceIndices)
            ? options.keepAliveFaceIndices
            : [];
        this.keepAliveFaceIndices = new Set(
            keepAliveFaceIndices
                .map((value: number) => Number(value))
                .filter((value: number) => Number.isFinite(value) && value >= 0)
                .map((value: number) => Math.floor(value))
        );
        this._dateFormatter = new Intl.DateTimeFormat('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
        });
        this._cachedDateKey = '';
        this._cachedDateLabel = '';
        const stored = this._getStorage().getItem('watchface');
        if (stored !== null) {
            const parsed = parseInt(stored, 10);
            if (Number.isFinite(parsed) && parsed >= 0) {
                this.activeIndex = parsed;
            }
        }
    }

    _getStorage(): StorageLike {
        // `window.Loop` is injected by the Loop WebView runtime and may include
        // a custom storage object — use it when available, fall back to localStorage.
        const w = window as unknown as { Loop?: { storage?: StorageLike } };
        if (typeof w.Loop !== 'undefined' && w.Loop?.storage) return w.Loop.storage;
        if (typeof localStorage !== 'undefined') return localStorage;
        return { getItem: () => null, setItem: () => {} };
    }

    register(FaceClass: FaceClass): void {
        this.registry.push(FaceClass);
    }

    getRegisteredFaces(): Array<{ name: string; index: number }> {
        return this.registry.map((FC, i) => ({ name: FC.name, index: i }));
    }

    _isKeepAliveIndex(index: number): boolean {
        return this.keepAliveFaceIndices.has(index);
    }

    _buildFaceOptions(): FaceOptions {
        return {
            width: this.container.clientWidth || 800,
            height: this.container.clientHeight || 800,
            dpr: Math.min(window.devicePixelRatio || 1, 3),
        };
    }

    _createLayer(): HTMLDivElement {
        const layer = document.createElement('div');
        layer.className = 'watchface-layer';
        layer.style.position = 'absolute';
        layer.style.inset = '0';
        layer.style.width = '100%';
        layer.style.height = '100%';
        layer.style.pointerEvents = 'none';
        return layer;
    }

    _hideLayer(layer: HTMLDivElement): void {
        layer.style.opacity = '0';
        layer.style.visibility = 'hidden';
    }

    _showLayer(layer: HTMLDivElement): void {
        if (layer.parentNode !== this.container) {
            this.container.appendChild(layer);
        } else {
            // Keep active face above any cached hidden layers.
            this.container.appendChild(layer);
        }
        layer.style.opacity = '1';
        layer.style.visibility = 'visible';
    }

    _getClockPayload(now: Date = new Date()): ClockPayload {
        const ms = now.getMilliseconds();
        const seconds = now.getSeconds() + ms / 1000;
        const minutes = now.getMinutes() + seconds / 60;
        const hours = (now.getHours() % 12) + minutes / 60;
        const dateKey = `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}`;
        if (dateKey !== this._cachedDateKey) {
            this._cachedDateKey = dateKey;
            this._cachedDateLabel = this._dateFormatter.format(now).toLowerCase();
        }
        return {
            hours,
            minutes,
            seconds,
            ms,
            date: now,
            hourDeg: hours * 30,
            minuteDeg: minutes * 6,
            secondDeg: seconds * 6,
            dateLabel: this._cachedDateLabel,
        };
    }

    _renderFaceNow(face: WatchfaceBase): void {
        face.update(this._getClockPayload(new Date()));
    }

    prewarmFace(index: number): boolean {
        const safeIndex = clamp(index, 0, Math.max(0, this.registry.length - 1));
        if (safeIndex === this.activeIndex && this.activeFace) return false;
        if (this._faceCache.has(safeIndex)) return false;
        const FaceClass = this.registry[safeIndex];
        if (!FaceClass) return false;

        const layer = this._createLayer();
        this.container.appendChild(layer);
        this._hideLayer(layer);

        const face = new FaceClass(layer, this._buildFaceOptions());
        face.mount();
        this._renderFaceNow(face);
        face.pause('prewarmed');

        this._faceCache.set(safeIndex, { face, layer });
        return true;
    }

    mountFace(index: number, options: MountFaceOptions = {}): void {
        const renderNow = options.renderNow !== false;
        const safeIndex = clamp(index, 0, Math.max(0, this.registry.length - 1));
        const FaceClass = this.registry[safeIndex];
        if (!FaceClass) return;

        if (this.activeFace && this.activeIndex === safeIndex) {
            if (this.activeLayer) this._showLayer(this.activeLayer);
            this.activeFace.resume(0);
            if (renderNow) this._renderFaceNow(this.activeFace);
            return;
        }

        const previousFace = this.activeFace;
        const previousLayer = this.activeLayer;
        const previousIndex = this.activeIndex;

        let nextFace: WatchfaceBase;
        let nextLayer: HTMLDivElement;
        const cached = this._faceCache.get(safeIndex);
        if (cached) {
            nextFace = cached.face;
            nextLayer = cached.layer;
            this._faceCache.delete(safeIndex);
        } else {
            nextLayer = this._createLayer();
            this.container.appendChild(nextLayer);
            nextFace = new FaceClass(nextLayer, this._buildFaceOptions());
            nextFace.mount();
        }

        this.activeIndex = safeIndex;
        this.activeFace = nextFace;
        this.activeLayer = nextLayer;
        this._showLayer(nextLayer);
        this.activeFace.resume(0);
        if (renderNow) this._renderFaceNow(this.activeFace);

        if (previousFace && previousLayer) {
            if (this._isKeepAliveIndex(previousIndex)) {
                this._hideLayer(previousLayer);
                previousFace.pause('inactive');
                this._faceCache.set(previousIndex, {
                    face: previousFace,
                    layer: previousLayer,
                });
            } else {
                previousFace.unmount();
                if (previousLayer.parentNode) {
                    previousLayer.parentNode.removeChild(previousLayer);
                }
                this._faceCache.delete(previousIndex);
            }
        }

        this._getStorage().setItem('watchface', String(safeIndex));
        if (this.options.onFaceChanged) {
            this.options.onFaceChanged(this.activeFace, FaceClass);
        }
    }

    startClockLoop(): void {
        if (this.clockRafId) return;
        const gen = ++this._clockGen;
        const frame = () => {
            if (gen !== this._clockGen) return; // stale loop after stop/restart
            if (this.activeFace) {
                this.activeFace.update(this._getClockPayload(new Date()));
            }
            if (gen === this._clockGen) {
                this.clockRafId = requestAnimationFrame(frame);
            }
        };
        this.clockRafId = requestAnimationFrame(frame);
    }

    stopClockLoop(): void {
        this._clockGen += 1;
        if (this.clockRafId) {
            cancelAnimationFrame(this.clockRafId);
            this.clockRafId = null;
        }
    }

    triggerSwap(): void {
        if (!this.activeFace) return;
        if (this.isSwapping || this.activeFace.isChronoActive()) return;
        if (this.registry.length <= 1) {
            this.activeFace.chronoOut(() => {
                if (this.activeFace) this.activeFace.chronoIn();
            });
            return;
        }
        this.isSwapping = true;
        const nextIndex = (this.activeIndex + 1) % this.registry.length;
        this.activeFace.chronoOut(() => {
            this.mountFace(nextIndex, { renderNow: false });
            if (this.activeFace) {
                this.activeFace.chronoIn();
                this._renderFaceNow(this.activeFace);
            }
            this.isSwapping = false;
        });
    }

    setParallax(x: number, y: number, source?: string): void {
        if (this.activeFace) this.activeFace.setParallax(x, y, source);
    }

    setZoomProgress(p: number): void {
        if (this.activeFace) this.activeFace.setZoomProgress(p);
    }

    setMinimalMode(p: number): void {
        if (this.activeFace) this.activeFace.setMinimalMode(p);
    }

    setLockLineColliders(segments: LockLineColliderSegment[], options: LockLineColliderOptions = {}): void {
        if (!this.activeFace || typeof this.activeFace.setLockLineColliders !== 'function') return;
        this.activeFace.setLockLineColliders(segments, options);
    }

    onMotion(data: unknown): void {
        if (this.activeFace) this.activeFace.onMotion(data);
    }

    pause(reason?: string): void {
        if (this.activeFace) this.activeFace.pause(reason);
    }

    resume(pausedMs?: number): void {
        if (this.activeFace) this.activeFace.resume(pausedMs);
    }

    buildDebugPanel(panelEl: HTMLElement): void {
        if (!this.activeFace) return;
        const FaceClass = this.registry[this.activeIndex];
        if (!FaceClass) return;
        const schema: WatchfaceConfigParam[] = FaceClass.getConfigSchema();
        if (!schema || schema.length === 0) return;
        const config = this.activeFace.getConfig();
        const existing = panelEl.querySelectorAll('.watchface-config-row');
        existing.forEach((el) => el.remove());
        const head = document.createElement('div');
        head.className = 'blur-debug-head watchface-config-row';
        head.textContent = FaceClass.name || 'face';
        panelEl.appendChild(head);
        schema.forEach((param: WatchfaceConfigParam) => {
            const row = document.createElement('div');
            row.className = 'blur-debug-row watchface-config-row';
            const label = document.createElement('label');
            label.className = 'blur-debug-label';
            label.textContent = param.label;
            const slider = document.createElement('input');
            slider.className = 'blur-debug-slider';
            slider.type = 'range';
            slider.min = String(param.min);
            slider.max = String(param.max);
            slider.step = String(param.step);
            slider.value = String(config[param.key] ?? param.default);
            const valueSpan = document.createElement('span');
            valueSpan.className = 'blur-debug-value';
            const decimals = param.decimals ?? 2;
            valueSpan.textContent = Number(slider.value).toFixed(decimals);
            slider.addEventListener('input', () => {
                const v = Number(slider.value);
                valueSpan.textContent = v.toFixed(decimals);
                if (this.activeFace) {
                    this.activeFace.setConfig(param.key, v);
                }
            });
            row.appendChild(label);
            row.appendChild(slider);
            row.appendChild(valueSpan);
            panelEl.appendChild(row);
        });
    }

    teardownDebugPanel(panelEl: HTMLElement): void {
        const rows = panelEl.querySelectorAll('.watchface-config-row');
        rows.forEach((el) => el.remove());
    }
}
